<template>
  <div>
    <div
      v-if="!isMobile"
      style="height: 70px; width: 100%;"
    />
    <div :style="backgroundImageStyleObject" />
    <v-card
      class="mx-auto"
      :style="widgetStyleObject"
    >
      <div v-show="loading">
        <v-skeleton-loader
          class="mt-12 mx-auto"
          width="100%"
          :height="iframeHeight"
          type="card"
        />
      </div>
      <iframe
        v-show="!loading"
        :height="iframeHeight"
        width="100%"
        style="border:none;"
        :src="iframeUrlAndParams"
      />
    </v-card>
  </div>
</template>

<script>
  import { loadLanguageAsync } from '@/i18n'
  import validationApiServices from '@/helpers/validation_endpoints'
  import miscUtils from '@/helpers/utils'

  export default {
    data () {
      return {
        clinicWidgetId: '',
        widgetType: '',
        loading: true,
        backgroundImageSrc: '',
        backgroundImageStyleObject: {},
      }
    },

    computed: {
      isMobile () {
        return miscUtils.isMobile()
      },
      iframeUrlAndParams () {
        if (!this.widgetType) { return } // Wait for mount...
        const iframeUrlAndParams = miscUtils.buildIframeUrl({
          widgetType: this.widgetType,
          clinicWidgetId: this.clinicWidgetId,
          envVariables: process.env, // NOTE: Passes in all, but only uses a few.
        })
        return iframeUrlAndParams
      },
      iframeHeight () {
        let iframeHeight
        const offset = 150
        if (this.isMobile) {
          iframeHeight = window.innerHeight
        } else {
          iframeHeight = window.innerHeight - offset
        }
        return iframeHeight
      },
      minHeight () {
        return '100%'
      },
      widgetStyleObject () {
        return {
          zIndex: '3',
          maxWidth: '474px',
        }
      },
      linearGradientDegree () {
        return '0deg'
      },
      linearGradientRgbaLeft () {
        const r = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_R_CHANNEL', '255')
        const g = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_G_CHANNEL', '255')
        const b = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_B_CHANNEL', '255')
        const a = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_A_CHANNEL', '0.7')
        return {
          r: r,
          g: g,
          b: b,
          a: a,
        }
      },
      linearGradientRgbaRight () {
        const r = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_R_CHANNEL', '255')
        const g = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_G_CHANNEL', '255')
        const b = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_B_CHANNEL', '255')
        const a = this.getLocalesOrDefault('BACKGROUND_IMAGE_LINEAR_GRADIENT_RIGHT_A_CHANNEL', '0.3')
        return {
          r: r,
          g: g,
          b: b,
          a: a,
        }
      },
    },

    async mounted () {
      const clinicWidgetId = this.$route.query.provider
      this.clinicWidgetId = clinicWidgetId

      // NOTE: Fail as fast as possible, which is here on our own hosted page.
      try {
        const response = await validationApiServices.getClinicLocales({ clinicWidgetId: clinicWidgetId })
        this.widgetType = response.data.data.attributes.widget_type
        const messages = response.data.data.attributes.clinic_widget_locales_as_json
        const fallbackLanguage = response.data.data.attributes.fallback_locales_language
        await loadLanguageAsync({ clinicWidgetId, messages, fallbackLanguage })
        this.backgroundImageSrc = this.$t('JUMBOTRON_BANNER_IMAGE')
      } catch (e) {
        this.$router.push('/not-found')
        throw e
      }

      try {
        this.setCustomThemeColor()
      } catch (e) {
        // pass
      }
      this.backgroundImageStyleObject = this.setBackgroundImageStyleObject()

      const pause = 1000
      const that = this
      setTimeout(function () {
        that.loading = false
      }, pause)
    },

    methods: {
      setCustomThemeColor () {
        /** See note for Funnel Widget in separate repo.
        */
        const _colorFromLocales = 'PROVIDER_THEME_COLOR'
        let customThemeColor = ''
        if (miscUtils.isValidHex(this.$t(_colorFromLocales))) {
          customThemeColor = this.$t(_colorFromLocales)
        }
        const isColorCustomized = (customThemeColor && !(customThemeColor === _colorFromLocales))
        if (isColorCustomized) {
          if (!customThemeColor.startsWith('#')) {
            customThemeColor = `#${customThemeColor}`
          }
          if (miscUtils.isValidHex(customThemeColor)) {
            this.$vuetify.theme.themes.light.primary = customThemeColor
          } else {
            // Add airbrake
          }
        }
      },
      getLocalesOrDefault (localesLookup, defaultValue) {
        let value = defaultValue
        const exists = this.$t(localesLookup) && this.$t(localesLookup) !== localesLookup
        if (exists) {
          value = this.$t(localesLookup)
        }
        return value
      },
      setBackgroundImageStyleObject () {
        let adaptiveHeightStyle = '100%'
        if (this.isMobile) {
          adaptiveHeightStyle = '0'
        }
        return {
          height: adaptiveHeightStyle,
          zIndex: '2',
          width: '100%',
          position: 'absolute',
          top: '0',
          left: '0',
          background: `linear-gradient(${this.linearGradientDegree}, rgba(${this.linearGradientRgbaLeft.r}, ${this.linearGradientRgbaLeft.g}, ${this.linearGradientRgbaLeft.b}, ${this.linearGradientRgbaLeft.a}), rgba(${this.linearGradientRgbaRight.r}, ${this.linearGradientRgbaRight.g}, ${this.linearGradientRgbaRight.b}, ${this.linearGradientRgbaRight.a})), url(${this.backgroundImageSrc})`,
          'background-size': 'cover',
          'background-repeat': 'no-repeat',
          'background-position': 'center center',
        }
      },
    },
  }
</script>
